// Pure status helpers for the Lifts register list (Session AC; three-band Session AD). The clean
// list shows a single "Next due" colour and a status dot, both computed from a lift's next service
// date and its LOLER examination due date on a THREE-BAND window: red when overdue or due within 30
// days, amber when due 31 to 90 days out, green beyond 90 days (or not yet set). JSX-free so the
// list page and its tests share one source of truth (the assetFilters.ts / customerForm.ts pattern).
// Dates are handled in UTC, the same discipline as assetFilters and the server-side compliance helper.

export type LiftTone = "red" | "amber" | "green";

const DAY_MS = 24 * 60 * 60 * 1000;

// Three-band thresholds (Session AD, Director-confirmed). Red: overdue OR due within 30 days. Amber:
// due 31 to 90 days out. Green: more than 90 days out, or not yet set. The amber outer edge stays at
// 90 days; the new red inner band (30 days) is what makes a lift due in 17 days read differently from
// one due in 85 days.
export const LIFT_RED_WINDOW_DAYS = 30;
export const LIFT_AMBER_WINDOW_DAYS = 90;

// Days from the start of `now`'s UTC day to the date (negative = the date is before today). null
// for a missing or unparseable date.
function daysFromToday(value: string | Date | null | undefined, now: Date): number | null {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return (d.getTime() - today) / DAY_MS;
}

// The colour for a single due date on the list (three-band): red when overdue (before today) or due
// within 30 days, amber when due 31 to 90 days out, green when further out. null for a
// missing/invalid date (the caller renders an empty state, never "--").
export function dueDateTone(value: string | Date | null | undefined, now: Date): LiftTone | null {
  const days = daysFromToday(value, now);
  if (days == null) return null;
  if (days <= LIFT_RED_WINDOW_DAYS) return "red"; // overdue (days < 0) or due within 30 days
  if (days <= LIFT_AMBER_WINDOW_DAYS) return "amber";
  return "green";
}

export interface LiftStatusInput {
  // The lift's LOLER examination due date (lolerDue, falling back to the certificate expiry).
  lolerDue?: string | Date | null;
  // The lift's next service date (the next scheduled PPM visit, else the projected next-service-due).
  nextServiceDue?: string | Date | null;
  // The lift has a missed service visit (a past due date with no service recorded since). When true
  // the dot is red regardless of how far off the NEXT upcoming visit is, since the next-due date is
  // the next UPCOMING visit and would otherwise hide the overdue state (Session AD, Item 1).
  serviceOverdue?: boolean;
}

// The single status dot (three-band): red if LOLER or service is overdue/due-within-30 OR the lift
// has a missed service visit; amber if either is due 31 to 90 days out; green when both are beyond
// 90 days OR not yet set (a missing date never raises the dot on its own).
export function liftStatusDot(input: LiftStatusInput, now: Date): LiftTone {
  if (input.serviceOverdue) return "red";
  const loler = dueDateTone(input.lolerDue, now);
  const service = dueDateTone(input.nextServiceDue, now);
  if (loler === "red" || service === "red") return "red";
  if (loler === "amber" || service === "amber") return "amber";
  return "green";
}

// Plain-English label + Tailwind colour for a tone (shared by the dot and the Next-due cell).
export const LIFT_TONE_META: Record<LiftTone, { label: string; text: string; dot: string }> = {
  red: { label: "Action needed", text: "text-ele-danger", dot: "bg-ele-danger" },
  amber: { label: "Due soon", text: "text-amber-400", dot: "bg-amber-400" },
  green: { label: "Up to date", text: "text-ele-green", dot: "bg-ele-green" },
};
